/**
 * @deprecated Use authApi from services/auth instead.
 *
 * This utility is maintained for backward compatibility.
 * New code should use:
 *
 * @example
 * // Instead of:
 * import authFetch from '../utils/authFetch';
 * const data = await authFetch('/api/data');
 *
 * // Use:
 * import { authApi } from '../services/auth';
 * const data = await authApi.get('/api/data');
 * // or
 * const data = await authApi.post('/api/data', body);
 */

import { jwtDecode } from "jwt-decode";
import { supabase } from "../lib/supabase";

const TOKEN_STORAGE_KEY = "access_token";
const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
const USER_STORAGE_KEY = "userData";

// Check if Supabase token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    // Add a small buffer (e.g., 60 seconds) to refresh before actual expiration
    return decoded.exp < currentTime + 60;
  } catch (error) {
    console.error("Failed to decode token:", error);
    return true;
  }
};

// Handle authentication failure - clear token and redirect
const handleAuthFailure = () => {
  console.warn(
    "Authentication required or token expired. Redirecting to login."
  );
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  window.location.href = "/sign-in?status=session-expired";
};

// Attempt to refresh the session
const refreshSession = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();

    if (error || !data.session) {
      console.warn("Failed to refresh session:", error);
      return null;
    }

    const { access_token, refresh_token } = data.session;

    // Update local storage
    localStorage.setItem(TOKEN_STORAGE_KEY, access_token);
    if (refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refresh_token);
    }

    return access_token;
  } catch (error) {
    console.error("Error refreshing session:", error);
    return null;
  }
};

/**
 * Fetch wrapper with Supabase authentication
 * Automatically includes auth headers and handles token expiration
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} - Response data or throws error
 * @deprecated Use authApi from services/auth instead
 */
const authFetch = async (url, options = {}) => {
  let token = localStorage.getItem(TOKEN_STORAGE_KEY);

  // Validate token before making request
  if (isTokenExpired(token)) {
    console.log("Token expired, attempting refresh...");
    token = await refreshSession();

    if (!token) {
      handleAuthFailure();
      throw new Error("Access token is expired and refresh failed");
    }
  }

  // Prepare fetch options with auth header
  const fetchOptions = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await fetch(url, fetchOptions);

    // Handle authentication failure
    if (response.status === 401) {
      // Try one more refresh if we get a 401, in case the token was valid but revoked/bad for some other reason
      console.log("Received 401, attempting one retry with refresh...");
      const newToken = await refreshSession();

      if (newToken) {
        // Retry the request with new token
        const retryOptions = {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${newToken}`,
          },
        };
        const retryResponse = await fetch(url, retryOptions);

        if (retryResponse.ok) {
           // Handle no content responses
          if (retryResponse.status === 204) return null;
          return await retryResponse.json();
        }
      }

      handleAuthFailure();
      throw new Error("Unauthorized - please log in again");
    }

    // Handle other HTTP errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `Request failed with status ${response.status}`,
      }));
      const errorMessage =
        errorData.error || errorData.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    // Handle no content responses
    if (response.status === 204) {
      return null;
    }

    // Return parsed JSON response
    return await response.json();
  } catch (error) {
    // Don't re-throw auth errors that already triggered redirect
    if (
      error.message.includes("Unauthorized") ||
      error.message.includes("expired")
    ) {
      throw error;
    }

    console.error("Auth fetch error:", error);
    throw error;
  }
};

export default authFetch;
